/**
 * DOM adapter: turn a page's waypoints into measured targets.
 *
 * Every block the shared renderer draws carries `data-block-id`; that is the
 * lookup key on the public site, the CMS canvas and Preview alike — but it must
 * be qualified with `[data-block]`. The CMS canvas wraps each block in its own
 * `[data-canvas-block]` element that repeats `data-block-id` (the Outline flyout
 * finds blocks by it), and an unqualified `querySelector` returns that outer
 * wrapper: a node that is not in the `[data-block]` list below, so `indexOf`
 * misses, `previousBlockOf` returns null, and every `previous-block` anchor
 * silently collapses onto the block itself — in the editor only. A `chapter`
 * waypoint on a block that contains `[data-chapters-section]` elements (the
 * Chapters block) contributes one target per section, so one block is three
 * chapters — exactly the prototype's landing page. Any other block with the
 * chapter role is one chapter. A waypoint whose block is not in the DOM is
 * skipped rather than failing the whole timeline.
 */
export function resolveWaypointTargets(root, waypoints, documentTop) {
    const measure = (element) => ({
        top: documentTop(element),
        height: element.offsetHeight ?? element.getBoundingClientRect().height,
    });
    const blocks = Array.from(root.querySelectorAll("[data-block]"));
    const previousBlockOf = (element) => {
        const index = blocks.indexOf(element);
        return index > 0 ? blocks[index - 1] : null;
    };
    const targets = [];
    for (const waypoint of waypoints) {
        const block = root.querySelector(`[data-block][data-block-id="${cssEscape(waypoint.blockId)}"]`);
        if (!block)
            continue;
        const previous = previousBlockOf(block);
        const previousMeasured = previous ? measure(previous) : null;
        const sections = waypoint.role === "chapter"
            ? Array.from(block.querySelectorAll("[data-chapters-section]"))
            : [];
        const elements = sections.length > 0 ? sections : [block];
        elements.forEach((element, index) => {
            const self = measure(element);
            targets.push({
                blockId: sections.length > 1 ? `${waypoint.blockId}#${index}` : waypoint.blockId,
                role: waypoint.role,
                self,
                // The first section's "previous block" is the block before the whole
                // Chapters block; later sections use the section before them.
                previousBlock: index > 0 ? measure(elements[index - 1]) : (previousMeasured ?? self),
            });
        });
    }
    // Document order, so chapter numbering follows the page rather than the
    // order roles were assigned in.
    return targets.sort((a, b) => a.self.top - b.self.top);
}
function cssEscape(value) {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        return CSS.escape(value);
    }
    return value.replace(/["\\]/g, "\\$&");
}
//# sourceMappingURL=waypoints.js.map